import { Button, Card, Col, Row } from 'react-bootstrap';
import { Datetime, QueryInputType, Validation, queryInput } from '@/utils';
import { Fcr, FcrFcrReporterEnum, FcrType, FcrTypeEdge, SortDirectionEnum } from '@/models/gen/graphql';
import { useEffect, useMemo, useRef } from 'react';

import EditModal from '@/components/EditModal/new';
import FormField from '@/components/FormField';
import RadioButton from '@/components/RadioButton';
import { SearchFcrTypesDocument } from '@/api/queries';
import Tippy from '@tippyjs/react';
import TippyWhen from '@/components/TippyWhen';
import { createActivityForTrip } from '@/api/services/trips/createActivityBulk';
import { createComponentState } from '@/state';
import deleteFcrBulk from '@/api/services/fcrs/deleteFcrBulk';
import { submitFcr } from '@/api/services/trips/createFcrBulk';
import useDb from '@/hooks/useDb';
import useOnChange from '@/hooks/useOnChange';
import { useSearchFcrs } from '@/api/services/fcrs/searchFcrs';

type EditFcrModalState = {
  show?: boolean;
  loading?: boolean;
  tripId?: string;
  servicerIataAirlineCode?: string;
  flightNumber?: string;
  scheduled?: string;
  fcrTypeId?: string;
  description?: string;
  fcrName?: string;
  cause?: string;
  fcrReporter?: FcrFcrReporterEnum;
  onSubmit?: (data?: any) => Promise<void>;
  onHide?: () => void;
};

const initEditFcrModalState: EditFcrModalState = {
  show: false,
  loading: false,
  tripId: '',
  servicerIataAirlineCode: '',
  flightNumber: '',
  scheduled: '',
  fcrTypeId: '',
  description: '',
  fcrName: '',
  cause: '',
  fcrReporter: null,
  onSubmit: async (): Promise<void> => undefined,
  onHide: (): void => undefined,
};

const FcrReportersLabelMap: Record<FcrFcrReporterEnum, string> = {
  FLIGHT_ATTENDANT: 'Flight Attendant',
  PILOT: 'Pilot',
  UNKNOWN: 'Unknown',
};
export const useEditFcrModal = createComponentState(initEditFcrModalState);

const EditFcr: any = (): JSX.Element => {
  const [state, setState] = useEditFcrModal(({ state, setState }) => [state, setState]);
  const {
    show = false,
    loading = false,
    tripId = '',
    servicerIataAirlineCode = '',
    flightNumber = '',
    scheduled = '',
    fcrTypeId = '',
    description = '',
    fcrName = '',
    cause = '',
    fcrReporter = null,
    onHide = () => undefined,
    onSubmit = async () => undefined,
  } = state;
  const onChange = useOnChange(setState);
  const deletedFcrs = useRef(false);
  const fcrTypeFilter = ['Accident', 'Incident', 'LostAndFound'];

  // Init State
  const [validity, valid] = useMemo((): [Record<string, { valid: boolean }>, boolean] => {
    const result: Record<string, { valid: boolean }> = {};
    result.description = {
      valid: (description || '').trim().length > 0 && Validation.isAlphanumeric(description),
    };
    result.cause = {
      valid: (cause || '').trim().length > 0 && Validation.isAlphanumeric(cause),
    };
    result.fcrTypeId = {
      valid: Validation.isValidUUID(fcrTypeId),
    };
    result.tripId = {
      valid: Validation.isValidUUID(tripId),
    };
    result.fcrReporter = {
      valid: fcrReporter !== null && Object.values(FcrFcrReporterEnum).includes(fcrReporter),
    };
    const isValid = !(Object.values(result).filter((item: { valid: boolean }): boolean => !item.valid).length > 0);
    return [result, isValid];
  }, [description, cause, fcrTypeId, tripId, fcrReporter]);

  // Init Queries
  const [{ data: searchFcrsData, loading: searchFcrsLoading }, { refetch: refetchFcrs }] = useSearchFcrs();
  const { rows = [] } = searchFcrsData || {};

  const { data: FcrTypesData = {} } = useDb('FcrTypes', SearchFcrTypesDocument);
  const getFcrTypesData: any = useMemo(
    (): { rows: FcrType[] } => ({
      rows: (FcrTypesData.searchFcrTypes?.fcrTypeConnection?.edges || []).map((edge: FcrTypeEdge): FcrType => edge.node),
    }),
    [FcrTypesData]
  );

  const FcrReporters = Object.entries(FcrFcrReporterEnum).map(([key, value]) => ({
    label: FcrReportersLabelMap[key],
    value,
  }));

  // Crud Handlers
  const handleDeleteFcrs = async (id: string, name: string, cause: string, description: string, fcr: any): Promise<void> => {
    await deleteFcrBulk([id]);
    const fcrActivityPayload: Array<{ tripId: string; comment: string; field: string }> = [
      {
        tripId,
        comment: `${name}|${cause}|${description}`,
        field: 'DeleteFcr',
      },
    ];

    await createActivityForTrip(fcrActivityPayload);
    deletedFcrs.current = true;
    refetchFcrs();
  };

  const handleSubmit = async (): Promise<void> => {
    setState((current) => ({ ...current, loading: true }));
    try {
      submitFcr({ tripId, fcrTypeId, description, cause, fcrName, fcrReporter });
      onSubmit?.();
      onHide?.();
    } catch (error) {
      console.error(error);
    }
    setState({ show: false, loading: false });
  };

  const handleHide = (): void => {
    if (deletedFcrs.current) {
      handleSubmit();
    } else {
      onHide?.();
      setState({ show: false });
    }
  };

  useEffect((): void => {
    if (!tripId) return;
    refetchFcrs([
      {
        tripId: queryInput(tripId),
        createdAt: queryInput([], QueryInputType.ISNOTNULL, SortDirectionEnum.Desc),
      },
    ]);
  }, [refetchFcrs, tripId]);

  const title = `Complaint Details - ${(servicerIataAirlineCode || '').toUpperCase()}${flightNumber || ''} ${
    scheduled ? new Datetime(scheduled).frontendDatetimeShort : ''
  }`;

  return (
    <EditModal
      name="EditFcr"
      icon="sv sv-fcr"
      show={show}
      onHide={handleHide}
      title={title}
      options={{ Footer: null }}
      loading={searchFcrsLoading || loading}
      size="xl"
    >
      <div className="d-flex flex-column gap-4">
        <Row>
          <span className="uppercase">Reported By:</span>
          <Row className="d-flex justify-content-start mt-3">
            {FcrReporters.map((reporter) => (
              <Col key={reporter.value} sm={2}>
                <RadioButton
                  onClick={() => setState((current) => ({ ...current, fcrReporter: reporter.value }))}
                  checked={fcrReporter === reporter.value}
                >
                  {FcrReportersLabelMap[reporter.value]}
                </RadioButton>
              </Col>
            ))}
          </Row>
        </Row>
        <Row>
          <span className="uppercase">Report Type:</span>
          <Row className="d-flex justify-content-center mt-3">
            {/* Radio Component */}
            {getFcrTypesData.rows
              .filter((type) => !fcrTypeFilter.includes(type.name))
              .map(
                (fcrType: FcrType, idx: number): JSX.Element => (
                  <Col key={idx}>
                    <RadioButton
                      onClick={() => setState((current) => ({ ...current, fcrTypeId: fcrType?.id, fcrName: fcrType?.name }))}
                      checked={fcrTypeId === fcrType.id}
                      disabled={fcrReporter === null}
                    >
                      {fcrType.name}
                    </RadioButton>
                  </Col>
                )
              )}
          </Row>
        </Row>
      </div>
      <div className="my-3">
        <FormField
          label="Description:"
          name="description"
          type="textarea"
          onChange={onChange}
          placeholder="Description"
          value={description || ''}
          valid={validity?.description?.valid}
          disabled={fcrReporter === null}
          feedback={fcrReporter === null ? 'Please select a reporter first.' : null}
          options={{
            feedback: {
              asTooltip: true,
            },
          }}
          condensed
        />
        <FormField
          label="Root Cause:"
          name="cause"
          type="textarea"
          onChange={onChange}
          placeholder="Root Cause"
          value={cause || ''}
          disabled={fcrReporter === null}
          feedback={fcrReporter === null ? 'Please select a reporter first.' : null}
          options={{
            feedback: {
              asTooltip: true,
            },
          }}
          valid={validity?.cause?.valid}
        />
        <TippyWhen isTrue={!valid} options={{ content: 'No changes have been made, or fields are invalid.' }}>
          <div className="d-flex justify-content-end">
            <Button className="d-block px-5" name="SUBMIT" variant="green" disabled={!valid} onClick={handleSubmit}>
              ADD COMPLAINT
            </Button>
          </div>
        </TippyWhen>
      </div>
      {/* Fcrs */}
      {rows.length > 0 && (
        <div className="Fcrs d-flex flex-column">
          <div className="Fcrs-Scroll-Container">
            {rows.map((fcr: Fcr, idx: number) => (
              <FcrCard
                key={idx}
                data={fcr}
                onDelete={() => handleDeleteFcrs(fcr?.id, fcr?.fcrType?.name, fcr?.cause, fcr?.description, fcr)}
              />
            ))}
          </div>
        </div>
      )}
    </EditModal>
  );
};

const FcrCard = ({ data: fcr, onDelete }: { data: Fcr; onDelete: (fcr: Fcr) => void }): JSX.Element => {
  return (
    <Card>
      <Card.Body>
        <Card.Title>{fcr?.fcrType?.name}</Card.Title>
        <Card.Subtitle>Reported By: {FcrReportersLabelMap[fcr?.fcrReporter] || fcr?.fcrReporter}</Card.Subtitle>
        <Card.Text>{fcr?.description}</Card.Text>
        {fcr?.cause && <Card.Subtitle className="mb-2 text-muted">Cause: {fcr?.cause}</Card.Subtitle>}
        {fcr?.creator?.firstName && fcr?.creator?.lastName && (
          <Card.Subtitle className="mb-2 text-muted">Created By: {`${fcr.creator.firstName} ${fcr.creator.lastName}`}</Card.Subtitle>
        )}
        <Card.Footer>
          <span className="card-footer-details">
            <Card.Text>{fcr?.createdAt ? new Datetime().setAsUTC(fcr?.createdAt).toLocaleDatetime().frontendDatetime : '--'}</Card.Text>
            <Tippy content="Delete Complaint">
              <Button name="DELETE" variant="transparent" onClick={(): void => onDelete(fcr)}>
                {<i className="sv sv-trash2 fs-5 text-danger" />}
              </Button>
            </Tippy>
          </span>
        </Card.Footer>
      </Card.Body>
    </Card>
  );
};

export default EditFcr;
